import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { extractError } from "../../lib/errors";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { FileText, Download } from "lucide-react";

/**
 * AuditReportTab — ISO 19011 §6.5 Report generation.
 * POST /audits/{id}/report/generate?locale=&format=docx
 * GET  /audits/{id}/report/download
 */
const AuditReportTab = ({ audit }) => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportMeta, setReportMeta] = useState(null);

  const locale = audit.locale || "es";

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/audits/${audit.audit_id}/report/generate?locale=${locale}&format=docx`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportMeta(res.data);
      toast.success(t("audits.report.generate_docx"));
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.plan_failed")));
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/audits/${audit.audit_id}/report/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit_report_${audit.audit_id}_${locale}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  const hasReport = !!audit.report_id || !!reportMeta;

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-6">
      <Card className="border-border w-full max-w-md">
        <CardContent className="pt-6 pb-6 flex flex-col items-center gap-4 text-center">
          <FileText className="w-12 h-12 text-muted-foreground" />
          <div>
            <p className="font-semibold">{t("audits.report.generate_docx")}</p>
            {reportMeta && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <Badge variant="secondary" className="text-xs">{locale.toUpperCase()}</Badge>
                <Badge variant="outline" className="text-xs">{reportMeta.format}</Badge>
                {reportMeta.size_bytes && (
                  <span className="text-xs text-muted-foreground">
                    {(reportMeta.size_bytes / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 w-full">
            <Button onClick={generateReport} disabled={generating}>
              <FileText className="w-4 h-4 mr-2" />
              {generating ? "..." : t("audits.report.generate_docx")}
            </Button>
            {hasReport && (
              <Button variant="outline" onClick={downloadReport} disabled={downloading}>
                <Download className="w-4 h-4 mr-2" />
                {downloading ? "..." : "Download .docx"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditReportTab;
